<template>
  <div class="teacher-survey">

    <!-- ── Pantalla de completado ───────────────────── -->
    <div v-if="completed" class="completed-screen">
      <v-row justify="center" align="center" style="min-height:80vh">
        <v-col cols="12" md="6" class="text-center">
          <v-icon size="64" color="success" class="mb-4">mdi-check-circle-outline</v-icon>
          <h2 class="completed-title mb-4">{{ $t('stepper.completed_title') }}</h2>
          <p class="completed-msg mb-6">{{ $t('stepper.completed_message') }}</p>
          <p class="completed-edit-note mb-6">{{ $t('stepper.edit_answers_note') }}</p>
          <v-btn color="primary" large elevation="0" class="mb-3 mr-2" @click="returnToForm">
            <v-icon left>mdi-pencil-outline</v-icon>
            {{ $t('stepper.edit_answers') }}
          </v-btn>
          <v-btn color="primary" outlined class="mb-3" @click="$router.push('/welcome')">
            {{ $t('stepper.return_home') }}
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <!-- ── Contenido principal del stepper ─────────── -->
    <template v-else>

      <!-- Barra de progreso superior -->
      <div class="progress-bar-wrap">
        <div class="progress-inner">
          <div
            class="progress-fill"
            :style="{ width: progressPercent + '%' }"
          ></div>
        </div>
        <span class="progress-label">
          Bloque {{ currentBlock }} de 6
        </span>
      </div>

      <!-- Tabs de bloques (no clicables, solo indicadores) -->
      <div class="steps-indicator">
        <div
          v-for="(step, i) in steps.slice(1)"
          :key="i"
          class="step-chip"
          :class="{
            'step-done':    i + 1 < currentBlock,
            'step-current': i + 1 === currentBlock,
            'step-future':  i + 1 > currentBlock
          }"
        >
          <v-icon x-small class="mr-1" v-if="i + 1 < currentBlock">mdi-check</v-icon>
          {{ step }}
        </div>
      </div>

      <!-- ── Contenedor del bloque activo ──────────── -->
      <v-container class="block-container" fluid>

        <!-- Título del bloque -->
        <div class="block-header mb-3">
          <h2 class="block-title">{{ steps[currentBlock] }}</h2>
        </div>

        <!-- Control de estado: Abierta / Cerrada (bloques 1-6) -->
        <div v-if="currentBlock >= 1" class="section-lock-bar mb-5">
          <v-tooltip bottom max-width="260">
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                v-bind="attrs"
                v-on="on"
                small
                :color="blockLocked[currentBlock] ? 'blue-grey' : 'success'"
                text-color="white"
                class="section-lock-chip"
                @click="toggleBlockLock(currentBlock)"
              >
                <v-icon small left>{{ blockLocked[currentBlock] ? 'mdi-lock' : 'mdi-lock-open-outline' }}</v-icon>
                {{ blockLocked[currentBlock] ? $t('active_source.section_closed') : $t('active_source.open_section') }}
              </v-chip>
            </template>
            <span>{{ blockLocked[currentBlock] ? $t('active_source.click_to_unlock') : $t('active_source.click_to_lock') }}</span>
          </v-tooltip>

          <div v-if="!blockLocked[currentBlock]" class="section-hint-bubble ml-3">
            <v-icon x-small color="#1565c0" class="mr-1" style="flex-shrink:0">mdi-information-outline</v-icon>
            <span>{{ $t('active_source.lock_hint') }}</span>
          </div>
        </div>

        <!-- Bloque 1: Red solar -->
        <div v-if="currentBlock === 1">
          <p class="block-subtitle mb-4">{{ $t('solar_block.subtitle') }}</p>

          <div v-if="!solarMode" class="text-center py-8 grey--text">
            <v-progress-circular indeterminate color="primary" />
            <p class="mt-3">Cargando preguntas...</p>
          </div>

          <template v-else>
            <div v-for="(q, idx) in solarQuestions" :key="q.id_question" class="solar-q-row mb-4">
              <v-card outlined class="solar-q-card pa-4">
                <div class="d-flex align-start">
                  <span class="q-index mr-3">{{ idx + 1 }}</span>
                  <div class="flex-grow-1">
                    <p class="q-text mb-3">{{ q[`Question_${$i18n.locale}`] }}</p>

                    <!-- Estado de la respuesta -->
                    <div class="answer-summary mb-2">
                      <template v-if="solarAnswers[q.id_question] === '__nobody__'">
                        <v-chip small color="grey lighten-3" text-color="grey darken-1">
                          <v-icon x-small left>mdi-account-off-outline</v-icon>
                          {{ $t('solar_block.nobody_registered') }}
                        </v-chip>
                      </template>
                      <template v-else-if="solarAnswers[q.id_question] && Object.keys(solarAnswers[q.id_question]).length">
                        <div
                          v-for="(reasons, personId) in solarAnswers[q.id_question]"
                          :key="personId"
                          class="d-flex align-center flex-wrap mb-1"
                        >
                          <!-- Nombre de la persona con botón de quitar -->
                          <v-chip
                            small
                            color="primary"
                            outlined
                            :close="!blockLocked[1]"
                            class="mr-1"
                            @click:close="removeSolarPerson(q.id_question, personId)"
                          >
                            <v-icon x-small left>mdi-account</v-icon>
                            {{ getEmployeeName(personId) }}
                          </v-chip>
                          <!-- Razones seleccionadas (checkboxes CHLA) -->
                          <template v-if="Array.isArray(reasons) && reasons.length">
                            <v-chip
                              v-for="reason in reasons"
                              :key="reason"
                              x-small
                              color="blue-grey lighten-4"
                              text-color="blue-grey darken-3"
                              class="mr-1"
                            >{{ getReasonLabel(q, reason) }}</v-chip>
                          </template>
                          <!-- Razón de texto libre (otros tipos) -->
                          <span
                            v-else-if="typeof reasons === 'string' && reasons.trim()"
                            class="text-caption grey--text"
                          >· {{ reasons }}</span>
                        </div>
                      </template>
                      <template v-else>
                        <span class="grey--text text-caption">Sin respuesta aún</span>
                      </template>
                    </div>

                    <v-btn
                      small
                      outlined
                      color="primary"
                      :disabled="blockLocked[1]"
                      @click="openSolarDialog(q)"
                    >
                      <v-icon left small>mdi-account-multiple-plus</v-icon>
                      {{ $t('solar_block.select_btn') }}
                    </v-btn>
                  </div>
                </div>
              </v-card>
            </div>

            <!-- Dialog de selección -->
            <select-persons-dialog
              :showDialog="showSolarDialog"
              :modalTitle="activeSolarQuestion ? activeSolarQuestion[`Question_${$i18n.locale}`] : ''"
              :questionID="activeSolarQuestion ? activeSolarQuestion.id_question : null"
              :responseType="activeSolarQuestion && activeSolarQuestion.question_possible_answers
                ? activeSolarQuestion.question_possible_answers.type
                : 'names_with_options'"
              :solarQuestion="activeSolarQuestion"
              :defaultOptions="activeSolarQuestion ? solarAnswers[activeSolarQuestion.id_question] : null"
              :maxSelections="activeSolarQuestion && activeSolarQuestion.question_possible_answers
                ? activeSolarQuestion.question_possible_answers.max_selections
                : null"
              fullscreen
              @personsSelected="onSolarPersonsSelected"
              @close="showSolarDialog = false"
            />
          </template>
        </div>

        <!-- Bloque 2: Modelo educativo -->
        <educational-model-block
          v-else-if="currentBlock === 2 && edModelMode"
          :network-mode-id="edModelMode.id_network_mode"
          :cycle-id="activeCycleId"
          :locked="blockLocked[2]"
          @completion-changed="edModelComplete = $event"
        />

        <!-- Bloque 3: Fricción de trabajo -->
        <workflow-friction-block
          v-else-if="currentBlock === 3"
          :cycle-id="activeCycleId"
          :locked="blockLocked[3]"
          @completion-changed="workflowComplete = $event"
        />

        <!-- Bloque 4: Cultura (sliders) -->
        <div v-else-if="currentBlock === 4">
          <p class="block-subtitle mb-5">{{ $t('culture_sliders.subtitle') }}</p>

          <div v-if="loadingCulture" class="d-flex justify-center py-8">
            <v-progress-circular indeterminate color="primary" />
          </div>

          <template v-else>
            <v-row>
              <v-col
                v-for="theme in cultureThemes"
                :key="theme.id"
                cols="12"
                md="6"
              >
                <v-card outlined class="culture-card pa-5 mb-4">
                  <h4 class="culture-card-title mb-4">
                    {{ theme[`Culture_mode_theme_${$i18n.locale}`] }}
                  </h4>

                  <p class="culture-prefix mb-3">
                    {{ theme[`Questions_prefix_${$i18n.locale}`] }}
                  </p>

                  <!-- Sliders Ahora -->
                  <p class="slider-section-label">{{ $t('culture_sliders.now_label') }}</p>
                  <auto-balance-sliders
                    v-model="cultureNow[theme.id]"
                    :descriptions="themeQuestionLabels(theme.id)"
                    :icons="quadrantIcons"
                    :locked="blockLocked[4]"
                    @change="onCultureChange(theme.id, 'now', $event)"
                    class="mb-4"
                  />

                  <!-- Sliders Preferido -->
                  <p class="slider-section-label">{{ $t('culture_sliders.preferred_label') }}</p>
                  <auto-balance-sliders
                    v-model="culturePreferred[theme.id]"
                    :descriptions="themeQuestionLabels(theme.id)"
                    :icons="quadrantIcons"
                    :locked="blockLocked[4]"
                    @change="onCultureChange(theme.id, 'preferred', $event)"
                  />
                </v-card>
              </v-col>
            </v-row>
          </template>
        </div>

        <!-- Bloque 5: Narrativas N1 + N2 -->
        <div v-else-if="currentBlock === 5">

          <!-- N1 -->
          <v-card outlined class="narrative-card pa-5 mb-5">
            <h4 class="narrative-card-title mb-1">{{ $t('narratives_block.n1_title') }}</h4>
            <p class="narrative-subtitle mb-4">{{ $t('narratives_block.n1_subtitle') }}</p>

            <!-- Narrativas N1 guardadas -->
            <v-alert
              v-for="entry in narrativesN1"
              :key="entry.id"
              outlined
              color="primary"
              dense
              class="mb-2 narrative-saved-entry"
            >
              <div class="d-flex align-start">
                <div class="flex-grow-1 narrative-entry-body">
                  <div class="mb-1" style="flex-wrap: wrap; display: flex;">
                    <v-chip x-small color="primary" class="mr-1">
                      {{ entry.title || '—' }}
                    </v-chip>
                  </div>
                  <span class="text-caption grey--text">
                    {{ entry.narrative ? entry.narrative.substring(0, 120) + (entry.narrative.length > 120 ? '…' : '') : '' }}
                  </span>
                </div>
                <v-btn
                  v-if="!blockLocked[5]"
                  icon x-small
                  class="ml-1 flex-shrink-0"
                  color="primary"
                  :title="$t('narratives_block.edit_narrative')"
                  @click="openEditNarrative('n1', entry)"
                >
                  <v-icon small>mdi-pencil-outline</v-icon>
                </v-btn>
                <v-btn
                  v-if="!blockLocked[5]"
                  icon x-small
                  class="ml-1 flex-shrink-0"
                  color="error"
                  :title="$t('narratives_block.delete_narrative')"
                  @click="deleteNarrative(entry.id)"
                >
                  <v-icon small>mdi-delete-outline</v-icon>
                </v-btn>
              </div>
            </v-alert>

            <add-narrative
              v-if="!blockLocked[5]"
              mode="create"
              :label="$t('narratives_block.n1_title')"
              :network_mode_id="narrativeMode ? narrativeMode.id_network_mode : null"
              :narrativeTopics="narrativeTopicsN1"
              :cycle-id="activeCycleId"
              narrative-type="n1"
              @new_narrative="onNewNarrative('n1', $event)"
            />
          </v-card>

          <!-- N2: título vacío + id_topic null → distinguible de N1 en analytics -->
          <v-card outlined class="narrative-card pa-5">
            <h4 class="narrative-card-title mb-1">{{ n2Title }}</h4>
            <p class="narrative-subtitle mb-4">{{ n2Subtitle }}</p>

            <!-- Narrativas N2 guardadas -->
            <v-alert
              v-for="entry in narrativesN2"
              :key="entry.id"
              outlined
              color="blue-grey"
              dense
              class="mb-2 narrative-saved-entry"
            >
              <div class="d-flex align-start">
                <span class="text-caption grey--text flex-grow-1">
                  {{ entry.narrative ? entry.narrative.substring(0, 120) + (entry.narrative.length > 120 ? '…' : '') : '' }}
                </span>
                <v-btn
                  v-if="!blockLocked[5]"
                  icon x-small
                  class="ml-1 flex-shrink-0"
                  color="primary"
                  :title="$t('narratives_block.edit_narrative')"
                  @click="openEditNarrative('n2', entry)"
                >
                  <v-icon small>mdi-pencil-outline</v-icon>
                </v-btn>
                <v-btn
                  v-if="!blockLocked[5]"
                  icon x-small
                  class="ml-1 flex-shrink-0"
                  color="error"
                  :title="$t('narratives_block.delete_narrative')"
                  @click="deleteNarrative(entry.id)"
                >
                  <v-icon small>mdi-delete-outline</v-icon>
                </v-btn>
              </div>
            </v-alert>

            <add-narrative
              v-if="!blockLocked[5]"
              mode="create"
              :label="n2Title"
              :network_mode_id="narrativeMode ? narrativeMode.id_network_mode : null"
              :narrativeTopics="narrativeTopicN2 ? [narrativeTopicN2] : []"
              :cycle-id="activeCycleId"
              narrative-type="n2"
              @new_narrative="onNewNarrative('n2', $event)"
            />
          </v-card>

          <!-- Diálogo de edición de narrativa (N1 o N2) -->
          <add-narrative
            v-if="editingNarrative"
            mode="edit"
            :show-narrative-dialog="showEditNarrativeDialog"
            :label="editingNarrativeType === 'n1' ? $t('narratives_block.n1_title') : n2Title"
            :network_mode_id="narrativeMode ? narrativeMode.id_network_mode : null"
            :narrativeTopics="editingNarrativeType === 'n1' ? narrativeTopicsN1 : (narrativeTopicN2 ? [narrativeTopicN2] : [])"
            :cycle-id="activeCycleId"
            :narrative-type="editingNarrativeType || 'n1'"
            :item="editingNarrative"
            @narrative_updated="onNarrativeUpdated"
            @close="closeEditNarrativeDialog"
          />
        </div>

        <!-- Bloque 6: Pregunta de cierre -->
        <div v-else-if="currentBlock === 6">
          <p class="block-subtitle mb-5">{{ $t('closing_block.optional_note') }}</p>

          <v-card outlined class="pa-5">
            <h4 class="narrative-card-title mb-3">{{ closingQuestion }}</h4>
            <v-textarea
              v-model="closingText"
              :placeholder="$t('closing_block.placeholder')"
              outlined
              auto-grow
              rows="5"
              :disabled="blockLocked[6]"
            ></v-textarea>
            <div class="d-flex justify-end mt-2">
              <v-btn
                color="primary"
                elevation="0"
                :loading="savingClosing"
                :disabled="blockLocked[6] || !closingText.trim()"
                @click="saveClosingVoice"
              >
                <v-icon left small>mdi-content-save-outline</v-icon>
                {{ $t('closing_block.save_draft_btn') }}
              </v-btn>
            </div>
          </v-card>
        </div>

      </v-container>

      <!-- ── Navegación inferior ────────────────────── -->
      <div class="nav-bar">
        <v-btn text @click="goBack" :disabled="currentBlock <= 1">
          <v-icon left>mdi-arrow-left</v-icon>
          {{ $t('stepper.previous') }}
        </v-btn>
        <v-spacer />
        <v-btn
          v-if="currentBlock < 6"
          color="primary"
          elevation="0"
          @click="advance"
        >
          {{ $t('stepper.next') }}
          <v-icon right>mdi-arrow-right</v-icon>
        </v-btn>
        <v-btn
          v-else
          color="success"
          elevation="0"
          :loading="finishing"
          @click="finish"
        >
          {{ $t('stepper.finish') }}
          <v-icon right>mdi-check</v-icon>
        </v-btn>
      </div>

    </template>
  </div>
</template>

<script>
import { useMainStore } from '@/store/main';
import { mapStores } from 'pinia';

import SelectPersonsDialog    from '@/components/SelectPersonsDialog.vue';
import EducationalModelBlock  from '@/components/EducationalModelBlock.vue';
import WorkflowFrictionBlock  from '@/components/WorkflowFrictionBlock.vue';
import AutoBalanceSliders     from '@/components/AutoBalanceSliders.vue';
import AddNarrative           from '@/components/AddNarrative.vue';

export default {
  name: 'TeacherSurvey',
  components: {
    SelectPersonsDialog,
    EducationalModelBlock,
    WorkflowFrictionBlock,
    AutoBalanceSliders,
    AddNarrative,
  },
  data() {
    return {
      finishing: false,
      completed: false,
      savingClosing: false,
      blockLocked: [false, false, false, false, false, false, false],

      // Block 6 – Cierre
      closingNarrativeId: null,

      // Block 1 – Solar
      solarQuestions: [],
      solarAnswers: {},
      showSolarDialog: false,
      activeSolarQuestion: null,

      // Block 2 – Modelo Educativo (completación recibida del hijo)
      edModelComplete: false,

      // Block 3 – Workflow (completación recibida del hijo)
      workflowComplete: false,

      // Block 4 – Cultura
      loadingCulture: false,
      cultureThemes: [],
      cultureThemeQuestions: {},   // { themeId: [q1, q2, q3, q4] }
      cultureNow:       {},
      culturePreferred: {},
      cultureTouched:   {},        // { themeId: true } — temas con interacción explícita

      // Block 5 – Narrativas
      narrativesN1: [],
      narrativesN2: [],
      editingNarrative: null,
      editingNarrativeType: null,
      showEditNarrativeDialog: false,

      // Block 6 – Cierre
      closingText: '',
    };
  },
  computed: {
    ...mapStores(useMainStore),
    currentBlock()   { return this.mainStore.current_block; },
    isOptionalBlock() { return this.currentBlock === 5; },
    progressPercent() { return Math.min((this.currentBlock / 6) * 100, 100); },
    activeCycleId() {
      const c = this.mainStore.filteredCycles[0];
      return c ? c.id_cycle : null;
    },
    steps() {
      return [
        this.$t('stepper.step0'),
        this.$t('stepper.step1'),
        this.$t('stepper.step2'),
        this.$t('stepper.step3'),
        this.$t('stepper.step4'),
        this.$t('stepper.step5'),
        this.$t('stepper.step6'),
      ];
    },
    solarMode() {
      return this.mainStore.network_modes.find(m => m.network.form_type === 'solar');
    },
    edModelMode() {
      return this.mainStore.network_modes.find(m => m.network.form_type === 'bimodal');
    },
    narrativeMode() {
      return this.mainStore.network_modes.find(
        m => m.network.form_type === 'narrativa' && m.network.code === 'NARRATIVA_CHLA'
      );
    },
    closingMode() {
      return this.mainStore.network_modes.find(
        m => m.network.form_type === 'narrativa' && m.network.code === 'CIERRE_CHLA'
      );
    },
    quadrantLabels() {
      const qs = this.mainStore.culture_quadrants;
      if (qs && qs.length) {
        return qs.map(q => q[`Culture_quadrant_${this.$i18n.locale}`] || q.Culture_quadrant_es);
      }
      return [];
    },
    quadrantDescriptions() {
      const qs = this.mainStore.culture_quadrants;
      if (qs && qs.length) {
        return qs.map(q => q[`Culture_quadrant_desc_${this.$i18n.locale}`] || q.Culture_quadrant_desc_es || null);
      }
      return [];
    },
    narrativeTopicsN1() {
      return (this.mainStore.narrative_topics || []).filter(t => t.id === 1);
    },
    narrativeTopicN2() {
      return (this.mainStore.narrative_topics || []).find(t => t.id === 2) || null;
    },
    n2Title() {
      if (this.narrativeTopicN2) {
        return this.narrativeTopicN2[`tema_${this.$i18n.locale}`]
          || this.narrativeTopicN2.tema_es
          || this.$t('narratives_block.n2_title');
      }
      return this.$t('narratives_block.n2_title');
    },
    n2Subtitle() {
      if (this.narrativeTopicN2) {
        return this.narrativeTopicN2[`topic_${this.$i18n.locale}`]
          || this.narrativeTopicN2.topic_es
          || this.$t('narratives_block.n2_subtitle');
      }
      return this.$t('narratives_block.n2_subtitle');
    },
    closingQuestion() {
      if (this.closingMode && this.closingMode.network_mode_theme) {
        return this.closingMode.network_mode_theme[`Network_mode_theme_${this.$i18n.locale}`]
          || this.closingMode.network_mode_theme.Network_mode_theme_es;
      }
      const t = (this.mainStore.narrative_topics || []).find(tp => tp.id === 3);
      if (t) {
        return t[`topic_${this.$i18n.locale}`] || t.topic_es || this.$t('closing_block.question');
      }
      return this.$t('closing_block.question');
    },
    quadrantIcons() {
      return ['mdi-account-group', 'mdi-lightbulb-on', 'mdi-trophy-outline', 'mdi-shield-check'];
    },
    blockValidationErrors() {
      switch (this.currentBlock) {
        case 1: {
          const missing = this.solarQuestions.filter(q => {
            const ans = this.solarAnswers[q.id_question];
            return ans === null || ans === undefined;
          }).map(q => q[`Question_${this.$i18n.locale}`] || q.Question_es || `Pregunta ${q.id_question}`);
          return missing;
        }
        case 2:
          return this.edModelComplete ? [] : [this.$t('validation.ed_model_incomplete')];
        case 3:
          return this.workflowComplete ? [] : [this.$t('validation.workflow_incomplete')];
        case 4: {
          const missing = this.cultureThemes.filter(t => !this.cultureTouched[t.id])
            .map(t => t[`Culture_mode_theme_${this.$i18n.locale}`] || t.Culture_mode_theme_es || `Tema ${t.id}`);
          return missing;
        }
        default:
          return [];
      }
    },
  },
  watch: {
    blockLocked: {
      deep: true,
      handler(val) {
        const key = `blockLocked_${this.mainStore.logged_user?.email || 'unknown'}`;
        localStorage.setItem(key, JSON.stringify(val));
      },
    },
    currentBlock(val, old) {
      if (this.completed && val !== old) this.completed = false;
      if (val === 1 && this.solarMode && !this.solarQuestions.length) {
        this.loadSolarQuestions();
      }
      if (val === 4 && !this.cultureThemes.length) {
        this.loadCultureThemes();
      }
      if (val === 5) {
        this.loadNarratives();
      }
    },
    // Cuando activeCycleId llega (puede ser después del mount si initialize() aún no terminó)
    async activeCycleId(val) {
      if (val && !this.mainStore.network_modes.length) {
        await this.mainStore.getNetworkModes(val);
      }
      // Re-intentar cargar lo que corresponda al bloque actual
      if (val && this.currentBlock === 1 && this.solarMode && !this.solarQuestions.length) {
        this.loadSolarQuestions();
      }
      if (val && this.currentBlock === 4 && !this.cultureThemes.length) {
        this.loadCultureThemes();
      }
    },
  },
  async mounted() {
    // Asegurar que los datos base están cargados si ya hay ciclo activo
    if (!this.mainStore.network_modes.length && this.activeCycleId) {
      await this.mainStore.getNetworkModes(this.activeCycleId);
    }
    this.mainStore.current_block = 1;

    // Cargar preguntas de Bloque 1 si el modo solar ya está disponible
    if (this.solarMode && !this.solarQuestions.length) {
      this.loadSolarQuestions();
    }

    // Cargar Is_concluded desde la BD para bloques 1, 2, 3, 5, 6 (IRA adjacency forms)
    if (this.activeCycleId) {
      try {
        const resp = await this.$axios.get(
          `${process.env.VUE_APP_BACKEND_URL}/user/${this.mainStore.logged_user.id}/cycle/${this.activeCycleId}/adjacency_input_forms`
        );
        const workflowModeId = (() => {
          const wm = this.mainStore.network_modes.find(m => m.network && m.network.block_order === 3);
          return wm ? wm.id_network_mode : null;
        })();
        (resp.data || []).forEach(form => {
          const modeId = form.id_network_mode;
          let blockIdx = null;
          if (this.solarMode     && modeId === this.solarMode.id_network_mode)     blockIdx = 1;
          else if (this.edModelMode   && modeId === this.edModelMode.id_network_mode)   blockIdx = 2;
          else if (workflowModeId     && modeId === workflowModeId)                      blockIdx = 3;
          else if (this.narrativeMode && modeId === this.narrativeMode.id_network_mode) blockIdx = 5;
          else if (this.closingMode   && modeId === this.closingMode.id_network_mode)   blockIdx = 6;
          if (blockIdx !== null) {
            this.$set(this.blockLocked, blockIdx, !!form.Is_concluded);
          }
        });
      } catch (e) { void e; }
    }

    // Cargar Is_concluded del bloque 4 (Cultura) desde CVF
    if (this.activeCycleId) {
      try {
        const cResp = await this.$axios.get(
          `${process.env.VUE_APP_BACKEND_URL}/culture/user/${this.mainStore.logged_user.id}/cycle/${this.activeCycleId}/concluded`
        );
        if (cResp.data?.is_concluded) {
          this.$set(this.blockLocked, 4, true);
        }
      } catch (e) { void e; }
    }
  },
  methods: {
    blockNetworkModeId(blockIndex) {
      switch (blockIndex) {
        case 1: return this.solarMode?.id_network_mode ?? null;
        case 2: return this.edModelMode?.id_network_mode ?? null;
        case 3: {
          const wm = this.mainStore.network_modes.find(m => m.network && m.network.block_order === 3);
          return wm ? wm.id_network_mode : null;
        }
        case 5: return this.narrativeMode?.id_network_mode ?? null;
        case 6: return this.closingMode?.id_network_mode ?? null;
        default: return null;
      }
    },

    async toggleBlockLock(index) {
      const newState = !this.blockLocked[index];
      this.$set(this.blockLocked, index, newState);

      if (index === 4) {
        if (this.activeCycleId) {
          try {
            await this.$axios.post(`${process.env.VUE_APP_BACKEND_URL}/culture/set_concluded`, {
              cycle_id:  this.activeCycleId,
              concluded: newState,
            });
          } catch (e) { console.error('[toggleBlockLock block 4] Error:', e); }
        }
        return;
      }

      const networkModeId = this.blockNetworkModeId(index);
      if (networkModeId && this.activeCycleId) {
        try {
          await this.$axios.post(`${process.env.VUE_APP_BACKEND_URL}/open_close_adjacency_input_form`, {
            cycle_id:        this.activeCycleId,
            network_mode_id: networkModeId,
            closed:          newState,
          });
        } catch (e) { console.error('[toggleBlockLock] Error guardando estado:', e); }
      }
    },

    advance() {
      this.mainStore.advanceBlock();
    },
    goBack()  { if (this.currentBlock > 1) this.mainStore.goToBlock(this.currentBlock - 1); },

    // ── Solar ────────────────────────────────────────
    async loadSolarQuestions() {
      const resp = await this.$axios.get(
        `${process.env.VUE_APP_BACKEND_URL}/network_mode/${this.solarMode.id_network_mode}/questions`
      );
      this.solarQuestions = resp.data;
      await this.loadSolarAnswers();
    },
    async loadSolarAnswers() {
      try {
        const resp = await this.$axios.get(
          `${process.env.VUE_APP_BACKEND_URL}/user/${this.mainStore.logged_user.id}/cycle/${this.activeCycleId}/responses`
        );
        resp.data.forEach(r => {
          if (r.adjacency_input_form.id_network_mode === this.solarMode.id_network_mode) {
            try {
              const parsed = JSON.parse(r.Response);
              const value = parsed && parsed[0] ? parsed[0].valor : null;
              this.$set(this.solarAnswers, r.id_question, value);
            } catch (e) { void e; }
          }
        });
      } catch (e) { void e; }
    },
    openSolarDialog(question) {
      this.activeSolarQuestion = question;
      this.showSolarDialog = true;
    },
    async onSolarPersonsSelected(data) {
      this.showSolarDialog = false;
      const { selected_persons, questionID } = data;

      // Guardar estado previo para rollback si falla el backend
      const previousAnswer = this.solarAnswers[questionID];
      this.$set(this.solarAnswers, questionID, selected_persons);

      if (selected_persons === '__nobody__') return;

      try {
        await this.$axios.post(`${process.env.VUE_APP_BACKEND_URL}/save_answer`, {
          cycle_id:        this.activeCycleId,
          user_email:      this.mainStore.logged_user.email,
          item_id:         this.mainStore.logged_user.id,
          question_id:     questionID,
          network_mode_id: this.solarMode.id_network_mode,
          selected_option: selected_persons,
        });
      } catch (e) {
        // Revertir la UI al estado anterior si el backend falla
        this.$set(this.solarAnswers, questionID, previousAnswer);
        console.error('[Solar] Error guardando respuesta Q4:', e?.response?.data || e.message);
        this.$alertify.error(this.$t('validation.finish_error'));
      }
    },
    getEmployeeName(personId) {
      const emp = this.mainStore.employees.find(e => String(e.id) === String(personId));
      return emp ? emp.username : personId;
    },

    getReasonLabel(question, value) {
      try {
        const opts = JSON.parse(
          question.question_possible_answers[`Question_possible_answers_${this.$i18n.locale}`]
        );
        const opt = opts.find(o => o.valor === value);
        return opt ? opt.texto : value;
      } catch { return value; }
    },

    async removeSolarPerson(questionId, personId) {
      const current = { ...this.solarAnswers[questionId] };
      delete current[personId];
      const newValue = Object.keys(current).length ? current : null;
      this.$set(this.solarAnswers, questionId, newValue);

      await this.$axios.post(`${process.env.VUE_APP_BACKEND_URL}/save_answer`, {
        cycle_id:        this.activeCycleId,
        user_email:      this.mainStore.logged_user.email,
        item_id:         this.mainStore.logged_user.id,
        question_id:     questionId,
        network_mode_id: this.solarMode.id_network_mode,
        selected_option: newValue,
      });
    },

    // ── Cultura ──────────────────────────────────────
    async loadCultureThemes() {
      this.loadingCulture = true;

      // Asegurar que los modos de cultura estén cargados (puede haber race condition en el arranque)
      if (!this.mainStore.culture_modes.length) {
        await this.mainStore.getCultureModes();
      }

      const modeId = this.mainStore.culture_modes[0]?.id;
      if (!modeId) { this.loadingCulture = false; return; }

      const resp = await this.$axios.get(
        `${process.env.VUE_APP_BACKEND_URL}/culture/mode/${modeId}/themes`
      );
      this.cultureThemes = resp.data;

      this.cultureThemes.forEach(t => {
        this.$set(this.cultureNow,       t.id, [0, 0, 0, 0]);
        this.$set(this.culturePreferred, t.id, [0, 0, 0, 0]);
      });

      // Cargar preguntas de cada tema en paralelo para poder mapear slider → question_id
      const questionResults = await Promise.all(
        this.cultureThemes.map(t =>
          this.$axios.get(`${process.env.VUE_APP_BACKEND_URL}/culture/theme/${t.id}/questions`)
        )
      );
      this.cultureThemes.forEach((t, i) => {
        this.$set(this.cultureThemeQuestions, t.id, questionResults[i].data);
      });

      // Cargar respuestas previas si existen
      await this.loadExistingCultureAnswers(modeId);

      this.loadingCulture = false;
    },

    async loadExistingCultureAnswers(modeId) {
      for (const theme of this.cultureThemes) {
        try {
          const resp = await this.$axios.get(
            `${process.env.VUE_APP_BACKEND_URL}/culture/user/${this.mainStore.logged_user.id}/cycle/${this.activeCycleId}/mode/${modeId}/theme/${theme.id}/answers`
          );
          if (resp.data && resp.data.length) {
            const questions = this.cultureThemeQuestions[theme.id] || [];
            const nowVals  = [0, 0, 0, 0];
            const prefVals = [0, 0, 0, 0];
            resp.data.forEach(ans => {
              const idx = questions.findIndex(q => q.id === ans.id_culture_mode_theme_question);
              if (idx !== -1) {
                nowVals[idx]  = ans.Actual    ?? 25;
                prefVals[idx] = ans.Preferred ?? 25;
              }
            });
            this.$set(this.cultureNow,       theme.id, nowVals);
            this.$set(this.culturePreferred, theme.id, prefVals);
            // Marcar como tocado: el usuario ya respondió este tema en una sesión anterior
            this.$set(this.cultureTouched, theme.id, true);
          }
        } catch (e) { void e; }
      }
    },

    async onCultureChange(themeId, type, values) {
      if (type === 'now') this.$set(this.cultureNow,       themeId, values);
      else               this.$set(this.culturePreferred,  themeId, values);
      // Registrar interacción explícita del usuario con este tema
      this.$set(this.cultureTouched, themeId, true);

      const questions = this.cultureThemeQuestions[themeId];
      if (!questions || !questions.length) {
        console.warn('[Cultura] No hay preguntas cargadas para tema', themeId, '— save omitido');
        return;
      }

      let modeId = this.mainStore.culture_modes[0]?.id;
      if (!modeId) {
        await this.mainStore.getCultureModes();
        modeId = this.mainStore.culture_modes[0]?.id;
        if (!modeId) {
          console.warn('[Cultura] culture_modes vacío — save omitido');
          return;
        }
      }

      if (!this.activeCycleId) {
        console.warn('[Cultura] activeCycleId es null — save omitido');
        return;
      }

      const nowVals  = this.cultureNow[themeId]      || [0, 0, 0, 0];
      const prefVals = this.culturePreferred[themeId] || [0, 0, 0, 0];

      const answers = questions.map((q, i) => ({
        question_id: q.id,
        now:         nowVals[i]  ?? 25,
        preferred:   prefVals[i] ?? 25,
      }));

      try {
        await this.$axios.post(`${process.env.VUE_APP_BACKEND_URL}/culture/save_answers`, {
          user_email: this.mainStore.logged_user.email,
          cycle_id:   this.activeCycleId,
          mode_id:    modeId,
          theme_id:   themeId,
          answers,
        });
      } catch (e) {
        console.error('[Cultura] Error guardando:', e?.response?.data || e.message);
        this.$alertify && this.$alertify.error('Error al guardar respuestas de cultura. Intenta de nuevo.');
      }
    },

    // ── Narrativas ───────────────────────────────────
    async loadNarratives() {
      try {
        const resp = await this.$axios.get(
          `${process.env.VUE_APP_BACKEND_URL}/user/${this.mainStore.logged_user.id}/cycle/${this.activeCycleId}/narratives`
        );
        const closingModeId = this.closingMode ? this.closingMode.id_network_mode : null;

        // Separar narrativas por tipo usando id_topic:
        //   N1 → id_topic === 1 (subtema predefinido) o id_topic === null con title no vacío (tema propio)
        //   N2 → id_topic === 2
        //   Cierre → red CIERRE_CHLA (se ignoran aquí)
        const all = resp.data || [];
        this.narrativesN1 = all.filter(n => {
          const modeId = n.adjacency_input_form_id
            ? n.adjacency_input_form_id
            : (n.network_mode_id || null);
          if (modeId === closingModeId) return false;
          if (n.title === 'Voz de cierre') return false;
          return n.id_topic === 1 || (n.id_topic == null && n.title && n.title.trim() !== '');
        });
        this.narrativesN2 = all.filter(n => {
          if (n.title === 'Voz de cierre') return false;
          return n.id_topic === 2;
        });

        // Cargar voz de cierre existente
        const closingEntry = all.find(n => n.title === 'Voz de cierre');
        if (closingEntry) {
          this.closingText          = closingEntry.narrative || '';
          this.closingNarrativeId   = closingEntry.id;
        }
      } catch (e) { void e; }
    },

    // Devuelve los textos de pregunta para un tema, ordenados según el orden de culture_quadrants
    themeQuestionLabels(themeId) {
      const questions = this.cultureThemeQuestions[themeId] || [];
      const locale    = this.$i18n.locale;
      return (this.mainStore.culture_quadrants || []).map(q => {
        const match = questions.find(tq => tq.id_culture_quadrant === q.id);
        return match
          ? (match[`Culture_mode_theme_question_${locale}`] || match.Culture_mode_theme_question_es || null)
          : null;
      });
    },

    async onNewNarrative(type, narrative) {
      // Recargar desde backend para garantizar que entry.id esté definido y coincida con el servidor
      await this.loadNarratives();
      void type; void narrative;
    },

    async deleteNarrative(id) {
      try {
        await this.$axios.delete(`${process.env.VUE_APP_BACKEND_URL}/user/narrative/delete`, {
          data: { narrative_id: id, user_email: this.mainStore.logged_user.email }
        });
      } catch (e) {
        console.error('Error eliminando narrativa:', e);
        return;
      }
      // Comparar como string para evitar mismatch number/string de IDs
      this.narrativesN1 = this.narrativesN1.filter(n => String(n.id) !== String(id));
      this.narrativesN2 = this.narrativesN2.filter(n => String(n.id) !== String(id));
    },

    openEditNarrative(type, entry) {
      this.editingNarrativeType = type;
      this.editingNarrative = { ...entry };
      this.showEditNarrativeDialog = true;
    },

    closeEditNarrativeDialog() {
      this.showEditNarrativeDialog = false;
      this.editingNarrative = null;
      this.editingNarrativeType = null;
    },

    async onNarrativeUpdated() {
      await this.loadNarratives();
      this.closeEditNarrativeDialog();
    },

    topicLabel(id) {
      const t = this.mainStore.narrative_topics.find(tp => tp.id === id);
      return t ? t[`topic_${this.$i18n.locale}`] : '—';
    },

    // ── Guardar voz de cierre (crear o actualizar) ───
    async saveClosingVoice() {
      if (!this.closingMode || !this.closingText.trim()) return;
      this.savingClosing = true;
      try {
        if (this.closingNarrativeId) {
          await this.$axios.post(`${process.env.VUE_APP_BACKEND_URL}/user/narrative/update`, {
            narrative_id:    this.closingNarrativeId,
            title:           'Voz de cierre',
            narrative:       this.closingText,
            cycle_id:        this.activeCycleId,
            user_email:      this.mainStore.logged_user.email,
            network_mode_id: this.closingMode.id_network_mode,
            id_topic:        null,
          });
        } else {
          const resp = await this.$axios.post(`${process.env.VUE_APP_BACKEND_URL}/user/narrative/add`, {
            title:           'Voz de cierre',
            narrative:       this.closingText,
            cycle_id:        this.activeCycleId,
            user_email:      this.mainStore.logged_user.email,
            network_mode_id: this.closingMode.id_network_mode,
            id_topic:        null,
          });
          if (resp.data?.response?.id) {
            this.closingNarrativeId = resp.data.response.id;
          }
        }
        this.mainStore.setFlashMessage({ message: this.$t('closing_block.saved_ok'), type: 'success' });
      } catch (e) {
        console.error('[saveClosingVoice] Error:', e);
      }
      this.savingClosing = false;
    },

    // ── Volver al formulario desde pantalla de completado ─
    returnToForm() {
      this.completed = false;
      this.mainStore.goToBlock(1);
    },

    // ── Finalizar ────────────────────────────────────
    async finish() {
      this.finishing = true;
      try {
        await this.saveClosingVoice();
        // Registrar completación en la BD: marca Is_concluded y crea registro de estado
        await this.$axios.post(`${process.env.VUE_APP_BACKEND_URL}/teacher_survey/complete`, {
          user_email: this.mainStore.logged_user.email,
          cycle_id:   this.activeCycleId,
        });
        this.mainStore.completeTeacherSurvey();
        this.completed = true;
      } catch (e) {
        console.error('[finish] Error al completar encuesta:', e);
        this.$alertify.error(this.$t('validation.finish_error'));
      }
      this.finishing = false;
    }
  }
}
</script>

<style scoped>
.teacher-survey { min-height: 100vh; background: #f8f9fb; }

/* ── Barra progreso ────────────────────────────────── */
.progress-bar-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 24px;
  background: white;
  border-bottom: 1px solid #e2e8f0;
}
.progress-inner {
  flex: 1;
  height: 6px;
  background: #e2e8f0;
  border-radius: 3px;
  overflow: hidden;
}
.progress-fill {
  height: 100%;
  background: #1e3a5f;
  border-radius: 3px;
  transition: width 0.4s ease;
}
.progress-label { font-size: 0.78rem; color: #6b7280; white-space: nowrap; }

/* ── Indicadores de paso ───────────────────────────── */
.steps-indicator {
  display: flex;
  gap: 6px;
  padding: 10px 24px;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  flex-wrap: wrap;
  overflow-x: hidden;
}
.step-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
  transition: all 0.2s;
}
.step-done    { background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; }
.step-current { background: #1e3a5f; color: white; }
.step-future  { background: #f3f4f6; color: #9ca3af; border: 1px solid #e5e7eb; }

/* ── Contenedor del bloque ─────────────────────────── */
.block-container {
  max-width: 960px;
  margin: 0 auto;
  padding-top: 24px !important;
  padding-bottom: 100px !important;
}

.block-header { display: flex; align-items: center; }
.block-title  { font-size: 1.35rem; font-weight: 700; color: #1e3a5f; }
.block-subtitle { font-size: 0.9rem; color: #4a5568; }

/* ── Bloque 1: Solar ───────────────────────────────── */
.solar-q-card { border-radius: 10px !important; }
.q-index {
  min-width: 28px; height: 28px;
  background: #1e3a5f; color: white;
  border-radius: 50%;
  font-size: 0.8rem; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.q-text { font-size: 0.925rem; color: #1f2937; font-weight: 500; margin: 0; }

/* ── Bloque 4: Cultura ─────────────────────────────── */
.culture-card     { border-radius: 10px !important; }
.culture-card-title { font-size: 1rem; font-weight: 700; color: #1e3a5f; }
.culture-prefix   { font-size: 0.875rem; color: #4a5568; font-style: italic; }
.slider-section-label {
  font-size: 0.78rem; font-weight: 700; color: #6b7280;
  text-transform: uppercase; letter-spacing: 0.06em;
  margin-bottom: 6px;
}

/* ── Bloque 5: Narrativas ──────────────────────────── */
.narrative-card       { border-radius: 10px !important; }
.narrative-card-title { font-size: 1rem; font-weight: 700; color: #1e3a5f; }
.narrative-subtitle   { font-size: 0.875rem; color: #4a5568; }
.narrative-entry      { display: flex; align-items: center; gap: 8px; }
.narrative-entry-body { min-width: 0; overflow: hidden; }
.narrative-entry-body ::v-deep .v-chip {
  max-width: 100%;
}
.narrative-entry-body ::v-deep .v-chip .v-chip__content {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}

/* ── Navegación inferior ───────────────────────────── */
.nav-bar {
  position: sticky;
  bottom: 0;
  display: flex;
  align-items: center;
  padding: 12px 24px;
  background: white;
  border-top: 1px solid #e2e8f0;
  z-index: 10;
}

/* ── Pantalla completado ───────────────────────────── */
.completed-screen     { padding: 40px 24px; }
.completed-title      { font-size: 1.6rem; font-weight: 700; color: #1e3a5f; }
.completed-msg        { font-size: 1rem; color: #4a5568; }
.completed-edit-note  { font-size: 0.875rem; color: #6b7280; }

/* ── Control de estado de sección ──────────────────── */
.section-lock-bar  { display: flex; align-items: center; flex-wrap: wrap; gap: 4px; }
.section-lock-chip { cursor: pointer; font-size: 0.78rem !important; font-weight: 600 !important; }

/* ── Burbuja de ayuda con flecha ─────────────────────── */
.section-hint-bubble {
  position: relative;
  display: inline-flex;
  align-items: center;
  background: #e3f2fd;
  border: 1px solid #90caf9;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 0.72rem;
  color: #1565c0;
  line-height: 1.4;
  max-width: 360px;
}
.section-hint-bubble::before {
  content: '';
  position: absolute;
  left: -8px;
  top: 50%;
  transform: translateY(-50%);
  border-width: 5px 8px 5px 0;
  border-style: solid;
  border-color: transparent #90caf9 transparent transparent;
}
.section-hint-bubble::after {
  content: '';
  position: absolute;
  left: -6px;
  top: 50%;
  transform: translateY(-50%);
  border-width: 4px 7px 4px 0;
  border-style: solid;
  border-color: transparent #e3f2fd transparent transparent;
}
</style>
